import type {
  CustomerApplicationFieldValueContract,
  CustomerApplicationSourceLinkContract,
  CustomerApplicationSourcePreviewContract,
  CustomerFieldMappingTarget,
  ListQuestionnaireSourceRecordsParams,
  ListQuestionnaireSourceRecordsResult,
  QuestionnaireSourceRecordArchiveStatus,
  QuestionnaireSourceRecordContract,
} from '@/contracts/customer-application-binding'
import type {
  CustomerApplicationContract,
  CustomerFieldMappingContract,
  CustomerProfileContract,
  DuplicateResolutionMode,
  ErpCustomerBackfillCompareContract,
  ListCustomerApplicationsParams,
  ListCustomerApplicationsResult,
  ListCustomersParams,
  ListCustomersResult,
  ReviewNotificationJobBatchProcessResult,
  ReviewNotificationJobContract,
  SalesUserContract,
  SalesUserFileImportPreviewRequest,
  SalesUserImportResultContract,
} from '@/contracts/customers'
import { createSyntheticMeta } from '@/contracts/questionnaire'
import type { CustomerApplication, ManagedCustomer, SalesUser } from '@/lib/business-workflow'
import {
  type CustomerDuplicateCandidate,
  createCustomerDuplicateCandidates,
} from '@/lib/customer-duplicate-candidates'
import {
  type CustomerApplicationManualCreateInput,
  type CustomerApplicationSourceCreateInput,
  archiveMockQuestionnaireSourceRecord,
  createMockCustomerApplicationFromSourceRecord,
  createMockManualCustomerApplication,
  getMockQuestionnaireSourceRecord,
  listMockQuestionnaireSourceRecords,
  previewMockCustomerApplicationFromSourceRecord,
  updateMockApplicationFieldValue,
} from '@/lib/mock-api/customer-application-binding'
import {
  getMockCustomer,
  getMockCustomerApplication,
  listMockCustomerApplications,
  listMockCustomers,
  listMockSalesUsers,
} from '@/lib/mock-api/customers'
import type { BusinessWorkflowSnapshot } from '@/lib/mock-api/persistent-store'
import { createDefaultBusinessWorkflowSnapshot, loadFixture } from '@/lib/mock-api/persistent-store'
import { primaryFormId } from '@/lib/mock-data'
import type { SessionUser } from '@/types/api'
import { readCustomerListPageCache, writeCustomerListPageCache } from '../customer-list-page-cache'
import {
  apiOrMock,
  buildQuery,
  fireAndForgetApi,
  metaWithSource,
  requestApiEnvelope,
  toSourceMeta,
} from './http'
import { isApiDataClientMode } from './mode'

export async function listSalesUsers(): Promise<{
  data: SalesUserContract[]
  meta: ReturnType<typeof toSourceMeta>
}> {
  return apiOrMock({
    label: 'listSalesUsers',
    api: async () => {
      const envelope = await requestApiEnvelope<SalesUserContract[]>('/api/sales-users')
      return { data: envelope.data, meta: toSourceMeta(envelope.meta) }
    },
    mock: listMockSalesUsers,
  })
}

export type SalesUsersSyncResultContract = {
  ok: boolean
  status: 'skipped' | 'synced' | 'failed'
  providerState: 'not_configured' | 'configured' | 'failed'
  errorCode?: string
  upstreamStatus?: number
  usersReceived: number
  pagesFetched: number
  adminsUpserted: number
  salesUsersUpserted: number
  skippedProtected: number
  message: string
}

export async function importSalesUsersCsvToApi(
  csv: string,
  supervisorCsv?: string,
): Promise<SalesUserImportResultContract> {
  const envelope = await requestApiEnvelope<SalesUserImportResultContract>(
    '/api/sales-users/import',
    {
      method: 'POST',
      body: JSON.stringify({ format: 'csv', content: csv, supervisorContent: supervisorCsv ?? '' }),
    },
  )
  return envelope.data
}

export async function syncSalesUsersFromWebhookToApi(): Promise<SalesUsersSyncResultContract> {
  const envelope = await requestApiEnvelope<SalesUsersSyncResultContract>('/api/sales-users/sync', {
    method: 'POST',
  })
  return envelope.data
}

export type SalesUserFileImportPreviewClientResult = {
  ok: boolean
  status: number
  code: string
  message: string
  providerState?: string
  acceptedFileName?: string
  rawPayloadIncluded: false
}

export async function previewSalesUserFileImportToApi(
  request: SalesUserFileImportPreviewRequest,
): Promise<SalesUserFileImportPreviewClientResult> {
  if (!isApiDataClientMode()) {
    return {
      ok: false,
      status: 503,
      code: 'provider_not_configured',
      message: '檔案解析供應商尚未啟用，沒有寫入 D1。',
      providerState: 'disabled',
      acceptedFileName: request.fileName,
      rawPayloadIncluded: false,
    }
  }

  const response = await fetch('/api/sales-users/import/files/preview', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  })
  const body = (await response.json().catch(() => null)) as {
    error?: string
    code?: string
    meta?: {
      providerState?: string
      acceptedMetadata?: { fileName?: string }
    }
  } | null

  return {
    ok: response.ok,
    status: response.status,
    code: body?.code ?? (response.ok ? 'ok' : 'provider_preview_failed'),
    message:
      body?.error ?? (response.ok ? '檔案匯入預覽完成。' : '檔案解析供應商尚未啟用，沒有寫入 D1。'),
    providerState: body?.meta?.providerState,
    acceptedFileName: body?.meta?.acceptedMetadata?.fileName ?? request.fileName,
    rawPayloadIncluded: false,
  }
}

export async function listCustomerApplications(
  params: ListCustomerApplicationsParams = {},
): Promise<ListCustomerApplicationsResult> {
  return apiOrMock({
    label: 'listCustomerApplications',
    api: async () => {
      const envelope = await requestApiEnvelope<CustomerApplicationContract[]>(
        `/api/customer-applications${buildQuery(params)}`,
      )
      return { data: envelope.data, meta: metaWithSource(envelope.meta) }
    },
    mock: () => listMockCustomerApplications(params),
  })
}

export async function getCustomerApplication(applicationId: string) {
  return apiOrMock({
    label: 'getCustomerApplication',
    api: async () => {
      const envelope = await requestApiEnvelope<CustomerApplicationContract>(
        `/api/customer-applications/${applicationId}`,
      )
      return {
        ...envelope.data,
        sourceMeta: toSourceMeta(envelope.meta ?? envelope.data.sourceMeta),
      }
    },
    mock: () => getMockCustomerApplication(applicationId),
  })
}

export async function listQuestionnaireSourceRecords(
  params: ListQuestionnaireSourceRecordsParams = {},
): Promise<ListQuestionnaireSourceRecordsResult> {
  return apiOrMock({
    label: 'listQuestionnaireSourceRecords',
    api: async () => {
      const envelope = await requestApiEnvelope<QuestionnaireSourceRecordContract[]>(
        `/api/customer-application-source-records${buildQuery(params)}`,
      )
      return { data: envelope.data, meta: metaWithSource(envelope.meta) }
    },
    mock: () => listMockQuestionnaireSourceRecords(params),
  })
}

export async function getQuestionnaireSourceRecord(sourceRecordId: string) {
  return apiOrMock({
    label: 'getQuestionnaireSourceRecord',
    api: async () => {
      const envelope = await requestApiEnvelope<QuestionnaireSourceRecordContract>(
        `/api/customer-application-source-records/${sourceRecordId}`,
      )
      return {
        ...envelope.data,
        sourceMeta: toSourceMeta(envelope.meta ?? envelope.data.sourceMeta),
      }
    },
    mock: () => getMockQuestionnaireSourceRecord(sourceRecordId),
  })
}

export async function archiveQuestionnaireSourceRecord(
  sourceRecordId: string,
  archiveStatus: QuestionnaireSourceRecordArchiveStatus,
) {
  return apiOrMock({
    label: 'archiveQuestionnaireSourceRecord',
    api: async () => {
      const envelope = await requestApiEnvelope<QuestionnaireSourceRecordContract>(
        `/api/customer-application-source-records/${sourceRecordId}/archive-status`,
        {
          method: 'PATCH',
          body: JSON.stringify({ archiveStatus }),
        },
      )
      return {
        ...envelope.data,
        sourceMeta: toSourceMeta(envelope.meta ?? envelope.data.sourceMeta),
      }
    },
    mock: () => archiveMockQuestionnaireSourceRecord(sourceRecordId, archiveStatus),
  })
}

export async function previewCustomerApplicationFromSourceRecord(
  sourceRecordId: string,
): Promise<CustomerApplicationSourcePreviewContract> {
  return apiOrMock({
    label: 'previewCustomerApplicationFromSourceRecord',
    api: async () => {
      const envelope = await requestApiEnvelope<CustomerApplicationSourcePreviewContract>(
        `/api/customer-applications/from-source-record/preview${buildQuery({ sourceRecordId })}`,
      )
      return {
        ...envelope.data,
        sourceMeta: toSourceMeta(envelope.meta ?? envelope.data.sourceMeta),
      }
    },
    mock: () => previewMockCustomerApplicationFromSourceRecord(sourceRecordId),
  })
}

export async function createCustomerApplicationFromSourceRecord(
  input: CustomerApplicationSourceCreateInput,
) {
  if (!isApiDataClientMode()) return createMockCustomerApplicationFromSourceRecord(input)

  const envelope = await requestApiEnvelope<CustomerApplicationContract>(
    '/api/customer-applications/from-source-record',
    {
      method: 'POST',
      body: JSON.stringify(input),
    },
  )
  return {
    ...envelope.data,
    sourceMeta: toSourceMeta(envelope.meta ?? envelope.data.sourceMeta),
  }
}

export async function createManualCustomerApplication(input: CustomerApplicationManualCreateInput) {
  if (!isApiDataClientMode()) return createMockManualCustomerApplication(input)

  const envelope = await requestApiEnvelope<CustomerApplicationContract>(
    '/api/customer-applications/manual',
    {
      method: 'POST',
      body: JSON.stringify(input),
    },
  )
  return {
    ...envelope.data,
    sourceMeta: toSourceMeta(envelope.meta ?? envelope.data.sourceMeta),
  }
}

export async function updateCustomerApplicationFieldValue(
  applicationId: string,
  fieldKey: CustomerFieldMappingTarget,
  nextValue: string,
) {
  if (!isApiDataClientMode()) {
    return updateMockApplicationFieldValue(applicationId, fieldKey, nextValue)
  }

  const envelope = await requestApiEnvelope<CustomerApplicationFieldValueContract[]>(
    `/api/customer-applications/${applicationId}/field-values/${fieldKey}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ value: nextValue }),
    },
  )
  return envelope.data
}

export async function reviewCustomerApplication(
  applicationId: string,
  status: CustomerApplication['status'],
  reviewNotes: string,
  duplicateDecision?: DuplicateResolutionMode,
) {
  const envelope = await requestApiEnvelope<CustomerApplicationContract>(
    `/api/customer-applications/${applicationId}/review`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        status,
        reviewNotes,
        duplicateDecision,
      }),
    },
  )
  return {
    ...envelope.data,
    sourceMeta: toSourceMeta(envelope.meta ?? envelope.data.sourceMeta),
  }
}

export async function listReviewNotificationJobs(
  params: { status?: string; limit?: number } = {},
): Promise<ReviewNotificationJobContract[]> {
  if (!isApiDataClientMode()) return []

  const envelope = await requestApiEnvelope<ReviewNotificationJobContract[]>(
    `/api/customer-applications/review-notification-jobs${buildQuery(params)}`,
  )
  return envelope.data
}

export async function processReviewNotificationJobBatch(
  limit = 5,
): Promise<ReviewNotificationJobBatchProcessResult> {
  if (!isApiDataClientMode()) {
    return { processedCount: 0, limit, idle: true, results: [] }
  }

  const envelope = await requestApiEnvelope<ReviewNotificationJobBatchProcessResult>(
    '/api/customer-applications/review-notification-jobs/process-batch',
    {
      method: 'POST',
      body: JSON.stringify({ limit }),
    },
  )
  return envelope.data
}

export async function retryReviewNotificationJob(jobId: string) {
  const envelope = await requestApiEnvelope(
    `/api/customer-applications/review-notification-jobs/${jobId}/retry-now`,
    {
      method: 'POST',
    },
  )
  return envelope.data
}

export async function listCustomers(
  params: ListCustomersParams = {},
): Promise<ListCustomersResult> {
  return apiOrMock({
    label: 'listCustomers',
    api: async () => {
      const envelope = await requestApiEnvelope<CustomerProfileContract[]>(
        `/api/customers${buildQuery(params)}`,
      )
      return { data: envelope.data, meta: metaWithSource(envelope.meta) }
    },
    mock: () => listMockCustomers(params),
  })
}

export async function listCustomersWithPageCache(
  params: ListCustomersParams = {},
): Promise<ListCustomersResult> {
  if (!isApiDataClientMode()) return listCustomers(params)
  const cached = readCustomerListPageCache(params)
  if (cached) return cached
  const result = await listCustomers(params)
  writeCustomerListPageCache(params, result)
  return result
}

export async function getCustomer(customerId: string) {
  return apiOrMock({
    label: 'getCustomer',
    api: async () => {
      const envelope = await requestApiEnvelope<CustomerProfileContract>(
        `/api/customers/${customerId}`,
      )
      return {
        ...envelope.data,
        sourceMeta: toSourceMeta(envelope.meta ?? envelope.data.sourceMeta),
      }
    },
    mock: () => getMockCustomer(customerId),
  })
}

export async function listCustomerDuplicateCandidates(customerId: string): Promise<{
  data: CustomerDuplicateCandidate[]
  meta: ReturnType<typeof metaWithSource>
}> {
  return apiOrMock({
    label: 'listCustomerDuplicateCandidates',
    api: async () => {
      const envelope = await requestApiEnvelope<CustomerDuplicateCandidate[]>(
        `/api/customers/${customerId}/duplicate-candidates`,
      )
      return { data: envelope.data, meta: metaWithSource(envelope.meta) }
    },
    mock: () => {
      const customers = loadFixture().businessWorkflow.customers
      const current = customers.find((customer) => customer.id === customerId)
      const candidates = current ? createCustomerDuplicateCandidates(current, customers) : []
      return {
        data: candidates,
        meta: metaWithSource({
          ...createSyntheticMeta(),
          total: candidates.length,
          candidate_source: 'synthetic_duplicate_candidates',
        }),
      }
    },
  })
}

export async function saveCustomerToApi(
  customerId: string,
  patch: Partial<ManagedCustomer>,
): Promise<CustomerProfileContract> {
  if (!isApiDataClientMode()) {
    const localCustomer = loadFixture().businessWorkflow.customers.find(
      (customer) => customer.id === customerId,
    )
    return {
      ...(localCustomer ?? ({} as ManagedCustomer)),
      ...patch,
      id: customerId,
      updatedAt: new Date().toISOString().slice(0, 10),
      sourceMeta: createSyntheticMeta(),
    }
  }

  const envelope = await requestApiEnvelope<CustomerProfileContract>(
    `/api/customers/${customerId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(patch),
    },
  )
  return {
    ...envelope.data,
    sourceMeta: toSourceMeta(envelope.meta ?? envelope.data.sourceMeta),
  }
}

export async function compareErpCustomerBackfill(
  customerId: string,
  erpCustomerCode: string,
): Promise<ErpCustomerBackfillCompareContract> {
  if (!isApiDataClientMode()) {
    return {
      customerId,
      erpCustomerCode,
      message: 'ERP 客戶資料查詢 webhook 尚未設定。',
      providerState: 'provider_not_configured',
      source: 'provider_not_configured',
      fields: [],
    }
  }

  const envelope = await requestApiEnvelope<ErpCustomerBackfillCompareContract>(
    `/api/customers/${customerId}/erp-backfill/compare`,
    {
      method: 'POST',
      body: JSON.stringify({ erpCustomerCode }),
    },
  )
  return envelope.data
}

export type BusinessWorkflowLoadOptions = {
  includeSalesUsers?: boolean
  includeApplications?: boolean
  includeCustomers?: boolean
  includeFieldMappings?: boolean
}

export async function loadBusinessWorkflowFromDataClient(
  options: BusinessWorkflowLoadOptions = {},
  baseSnapshot?: Partial<BusinessWorkflowSnapshot>,
): Promise<BusinessWorkflowSnapshot> {
  return apiOrMock({
    label: 'loadBusinessWorkflowFromDataClient',
    api: async () => {
      const local = {
        ...createDefaultBusinessWorkflowSnapshot(),
        ...baseSnapshot,
      }
      const {
        includeSalesUsers = true,
        includeApplications = true,
        includeCustomers = true,
        includeFieldMappings = true,
      } = options
      const [salesUsers, applications, customers, fieldMappings] = await Promise.all([
        includeSalesUsers
          ? listSalesUsers()
          : Promise.resolve({ data: local.salesUsers, meta: createSyntheticMeta() }),
        includeApplications
          ? listCustomerApplications({ limit: 100 })
          : Promise.resolve({
              data: local.applications as CustomerApplicationContract[],
              meta: createSyntheticMeta(),
            }),
        includeCustomers
          ? listCustomers({ limit: 100 })
          : Promise.resolve({ data: local.customers, meta: createSyntheticMeta() }),
        includeFieldMappings
          ? listCustomerFieldMappings(primaryFormId)
          : Promise.resolve({ data: local.fieldMappings, meta: createSyntheticMeta() }),
      ])
      return {
        ...local,
        salesUsers: salesUsers.data,
        applications: applications.data,
        customers: customers.data,
        fieldMappings: fieldMappings.data,
        sourceLinks: includeApplications
          ? buildSourceLinksFromApplicationContracts(applications.data)
          : local.sourceLinks,
        fieldValues: includeApplications
          ? buildFieldValuesByApplicationId(applications.data)
          : local.fieldValues,
      }
    },
    mock: () => loadFixture().businessWorkflow,
  })
}

function buildFieldValuesByApplicationId(applications: CustomerApplicationContract[]) {
  const fieldValues: Record<string, CustomerApplicationFieldValueContract[]> = {}
  for (const application of applications) {
    if (!application.fieldValues) continue
    fieldValues[application.id] = application.fieldValues
  }
  return fieldValues
}

function buildSourceLinksFromApplicationContracts(
  applications: CustomerApplicationContract[],
): CustomerApplicationSourceLinkContract[] {
  return applications.flatMap((application) => {
    const sourceSummary = application.sourceSummary
    if (
      sourceSummary?.sourceType !== 'questionnaire_record' ||
      !sourceSummary.sourceRecordId ||
      !sourceSummary.responseId ||
      !sourceSummary.formId
    ) {
      return []
    }

    return [
      {
        id: `api-source-link-${application.id}`,
        workspaceId: 'api-shared-workspace',
        applicationId: application.id,
        sourceRecordId: sourceSummary.sourceRecordId,
        responseId: sourceSummary.responseId,
        formId: sourceSummary.formId,
        sourceType: 'questionnaire_record',
        status: 'active',
        createdAt: sourceSummary.submittedAt ?? application.submittedAt,
        createdByEmail: application.submittedByEmail,
        sourceMeta: application.sourceMeta ?? createSyntheticMeta(),
      },
    ]
  })
}

export async function listCustomerFieldMappings(formId: string): Promise<{
  data: CustomerFieldMappingContract[]
  meta: ReturnType<typeof toSourceMeta>
}> {
  return apiOrMock({
    label: 'listCustomerFieldMappings',
    api: async () => {
      const envelope = await requestApiEnvelope<CustomerFieldMappingContract[]>(
        `/api/forms/${formId}/customer-field-mappings`,
      )
      return { data: envelope.data, meta: toSourceMeta(envelope.meta) }
    },
    mock: () => ({
      data: loadFixture().businessWorkflow.fieldMappings.filter((item) => item.formId === formId),
      meta: createSyntheticMeta(),
    }),
  })
}

export function syncSalesUserToApi(user: SalesUser) {
  fireAndForgetApi('syncSalesUserToApi', () =>
    requestApiEnvelope(`/api/sales-users/${user.id}`, {
      method: 'PATCH',
      body: JSON.stringify(user),
    }),
  )
}

export async function saveSalesUserToApi(user: SalesUser): Promise<SalesUserContract> {
  if (!isApiDataClientMode()) {
    return {
      ...user,
      id: user.id || `sales-${crypto.randomUUID()}`,
      sourceMeta: createSyntheticMeta(),
    }
  }

  const isExistingD1User =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(user.id)
  const envelope = await requestApiEnvelope<SalesUserContract>(
    isExistingD1User ? `/api/sales-users/${user.id}` : '/api/sales-users',
    {
      method: isExistingD1User ? 'PATCH' : 'POST',
      body: JSON.stringify(user),
    },
  )
  return envelope.data
}

export function syncCustomerApplicationToApi(
  input: Partial<CustomerApplication>,
  _currentUser: SessionUser | null,
) {
  fireAndForgetApi('syncCustomerApplicationToApi', () =>
    requestApiEnvelope('/api/customer-applications', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  )
}

export function syncApplicationReviewToApi(
  applicationId: string,
  status: CustomerApplication['status'],
  reviewNotes: string,
) {
  fireAndForgetApi('syncApplicationReviewToApi', () =>
    requestApiEnvelope(`/api/customer-applications/${applicationId}/review`, {
      method: 'PATCH',
      body: JSON.stringify({ status, reviewNotes }),
    }),
  )
}

export function syncCustomerToApi(customerId: string, patch: Partial<ManagedCustomer>) {
  fireAndForgetApi('syncCustomerToApi', () =>
    requestApiEnvelope(`/api/customers/${customerId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  )
}

export function syncCustomerFieldMappingsToApi(
  formId: string,
  mappings: CustomerFieldMappingContract[],
) {
  fireAndForgetApi('syncCustomerFieldMappingsToApi', () =>
    requestApiEnvelope(`/api/forms/${formId}/customer-field-mappings`, {
      method: 'PUT',
      body: JSON.stringify(mappings),
    }),
  )
}
